import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Tag, Folder, Trash2, Pencil, Check, X } from "lucide-react";

const CATEGORY_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6",
  "#3b82f6", "#6366f1", "#a855f7", "#ec4899", "#78716c",
];

const EditableItem = ({ item, onUpdate, onDelete, type }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [color, setColor] = useState(item.color);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onUpdate(item.id, { name: name.trim(), color });
      setIsEditing(false);
    } catch {} finally { setSaving(false); }
  };

  const handleCancel = () => {
    setName(item.name);
    setColor(item.color);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 space-y-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-3.5 py-2.5 text-[13px] text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.2] transition-colors"
          autoFocus
          data-testid={`edit-${type}-name-input`}
        />
        <div className="flex items-center gap-2 flex-wrap">
          {CATEGORY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`w-7 h-7 rounded-full border-2 transition-all duration-150 ${
                color === c ? "border-white scale-110" : "border-transparent hover:border-white/30"
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-black text-[12px] font-medium rounded-lg transition-all hover:shadow-lg disabled:opacity-40"
            data-testid={`save-${type}-btn`}
          >
            <Check size={13} />
            {saving ? "..." : "Enregistrer"}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.06] text-[#999] text-[12px] rounded-lg transition-all hover:text-white"
          >
            <X size={13} />
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between py-3 px-4 bg-white/[0.02] rounded-xl border border-white/[0.05] hover:border-white/[0.08] transition-colors group">
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="text-[13px] text-white">{name}</span>
      </div>
      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setIsEditing(true)}
          className="p-2 rounded-lg text-[#555] hover:text-white hover:bg-white/[0.06] transition-all"
          title="Modifier"
          data-testid={`edit-${type}-btn-${item.id}`}
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-2 rounded-lg text-[#555] hover:text-red-400 hover:bg-red-500/10 transition-all"
          title="Supprimer"
          data-testid={`delete-${type}-btn-${item.id}`}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};

const SettingsPage = ({ categories, tags, onCategoriesChange, onTagsChange }) => {
  const { api } = useAuth();

  const updateCategory = async (id, data) => {
    await api.patch(`/categories/${id}`, data);
    onCategoriesChange(categories.map((c) => c.id === id ? { ...c, ...data } : c));
  };

  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    onCategoriesChange(categories.filter((c) => c.id !== id));
  };

  const updateTag = async (id, data) => {
    await api.patch(`/tags/${id}`, data);
    onTagsChange(tags.map((t) => t.id === id ? { ...t, ...data } : t));
  };

  const deleteTag = async (id) => {
    await api.delete(`/tags/${id}`);
    onTagsChange(tags.filter((t) => t.id !== id));
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl font-bold text-white tracking-tight">Paramètres</h1>
        <p className="text-[#444] text-[13px] mt-1">Gérez vos catégories et tags</p>
      </div>

      {/* Categories Section */}
      <div className="mb-10">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Folder size={16} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold text-white">Catégories</h2>
            <p className="text-[11px] text-[#555]">{categories.length} catégorie{categories.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="space-y-2">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-[#444] text-[13px] bg-white/[0.02] rounded-xl border border-white/[0.05]">
              Aucune catégorie créée
            </div>
          ) : (
            categories.map((cat) => (
              <EditableItem
                key={cat.id}
                item={cat}
                onUpdate={updateCategory}
                onDelete={deleteCategory}
                type="category"
              />
            ))
          )}
        </div>
      </div>

      {/* Tags Section */}
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Tag size={16} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold text-white">Tags</h2>
            <p className="text-[11px] text-[#555]">{tags.length} tag{tags.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        <div className="space-y-2">
          {tags.length === 0 ? (
            <div className="text-center py-8 text-[#444] text-[13px] bg-white/[0.02] rounded-xl border border-white/[0.05]">
              Aucun tag créé
            </div>
          ) : (
            tags.map((tag) => (
              <EditableItem
                key={tag.id}
                item={tag}
                onUpdate={updateTag}
                onDelete={deleteTag}
                type="tag"
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
